import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertTerminalBody, Terminal, UpdateTerminalBody } from "../../../../../http/patrol/types/terminals";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { useTerminalsApi } from "../../../../../http/patrol/useTerminals";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface TerminalFormBody {
  item: AdminItem | null;
  forLoop: boolean;
  forNotLoop: boolean;
  supportMaterialIds: string[];
  systemIds: string[];
  visibility: VisibilityScopeValue;
}

interface TerminalDetailsProps {
  terminal?: Terminal;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const TerminalForm: FC<TerminalDetailsProps> = ({ terminal, isDisabled, isLoading, onClose }: TerminalDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const patrolSystemsApi = usePatrolSystemsApi();
  const supportMaterialsApi = usePatrolSteelVersionsApi();
  const terminalsApi = useTerminalsApi();

  const supportMaterials = supportMaterialsApi.useAdminMaterials({}, aborter.current.signal);

  const patrolSystems = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);

  const defaultValues: TerminalFormBody = {
    item: terminal?.item ?? null,
    forLoop: terminal?.forLoop ?? false,
    forNotLoop: terminal?.forNotLoop ?? false,
    supportMaterialIds: terminal?.supportMaterials.map((x) => x.id) ?? [],
    systemIds: terminal?.systems.map((x) => x.id) ?? [],
    visibility: terminal?.visibility ?? "hidden",
  };

  const onSubmit = () => async (formData: TerminalFormBody) => {
    setIsSubmitting(true);

    const update: UpdateTerminalBody = {
      forLoop: !!formData.forLoop,
      forNotLoop: !!formData.forNotLoop,
      supportMaterialIds: formData?.supportMaterialIds ?? null,
      systemIds: formData.systemIds ?? [],
      visibility: formData.visibility,
    };
    if (terminal?.item.id) {
      try {
        await terminalsApi.updateAdminTerminal(terminal?.item.id, update);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const inseert: InsertTerminalBody = {
        itemId: formData.item?.id!,
        ...update,
      };

      try {
        await terminalsApi.insertAdminTerminal(inseert);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<TerminalFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isLoading || isDisabled}
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, setValue, watch, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [terminal]);

        const forLoop = !!watch("forLoop");
        const forNotLoop = !!watch("forNotLoop");
        const supportMaterialIds = watch("supportMaterialIds");
        const systemIds = watch("systemIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        isLoading={isLoading}
                        isDisabled={isLoading || isDisabled || !!terminal?.item}
                        getOptionValue={(x) => x.id}
                        getOptionLabel={(x) => x.code}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.forLoop} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={forLoop}
                  disabled={isDisabled}
                  onChange={(checked) => setValue("forLoop", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.forNotLoop} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={forNotLoop}
                  disabled={isDisabled}
                  onChange={(checked) => setValue("forNotLoop", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.materials.plural}>
              <MultiCheckbox
                disabled={isDisabled}
                values={
                  supportMaterials.data?.items.map((x) => ({
                    id: x.id,
                    title: x.name,
                    selected: supportMaterialIds.includes(x.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue(
                    "supportMaterialIds",
                    checked ? [...supportMaterialIds, id] : supportMaterialIds.filter((x) => x !== id),
                  );
                }}
              />
            </FormField>
            <FormField label={i18n.translation.systems.plural}>
              <MultiCheckbox
                disabled={isDisabled}
                values={
                  patrolSystems.data?.items
                    .filter((x) => x.isLifeLine)
                    .map((x) => ({
                      id: x.id,
                      title: x.name,
                      selected: systemIds.includes(x.id),
                    })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((x) => x !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default TerminalForm;
